import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  createIamUser,
  listEmployees,
  listIamUsers,
  updateIamUserRoles,
  updateIamUserStatus,
  type EmployeeRecord,
  type IamUser,
} from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { Icon } from '../components/Icon';
import { StatusBadge } from '../components/StatusBadge';

const ROLE_INFO: Record<string, { label: string; icon: any; color: string; desc: string }> = {
  organization_owner: {
    label: 'Organisation Owner',
    icon: 'shieldCheck',
    color: '#f59e0b',
    desc: 'Full enterprise sovereignty, global settings, audit logs & bulk ingestion',
  },
  ceo_director: {
    label: 'CEO / Director',
    icon: 'dashboard',
    color: '#8b5cf6',
    desc: 'Executive KPIs, P&L oversight, department budgets & strategic controls',
  },
  hr_admin: {
    label: 'HR Administrator',
    icon: 'employees',
    color: '#3b82f6',
    desc: 'Workforce records, recruitment ATS, template vault, assets & lifecycle',
  },
  finance_manager: {
    label: 'Finance Manager',
    icon: 'finance',
    color: '#10b981',
    desc: 'General ledger, bank/cash treasury, AP/AR invoicing & cost center budgets',
  },
  payroll_admin: {
    label: 'Payroll Admin',
    icon: 'payroll',
    color: '#06b6d4',
    desc: 'Salary revisions, payroll calculation engine, statutory filings & payslips',
  },
  manager: {
    label: 'Team Lead / Manager',
    icon: 'briefcase',
    color: '#ec4899',
    desc: 'Direct report attendance rosters, leave & expense approvals, interview scorecards',
  },
  employee: {
    label: 'Employee (Self-Service)',
    icon: 'userCheck',
    color: '#64748b',
    desc: 'Personal attendance punch, leave requests, payslip download & expense claims',
  },
  auditor: {
    label: 'Compliance Auditor',
    icon: 'audit',
    color: '#6366f1',
    desc: 'Read-only financial & workforce compliance inspection with audit logs',
  },
};

export function AccessControl() {
  const { session } = useAuth();
  const [users, setUsers] = useState<IamUser[]>([]);
  const [employees, setEmployees] = useState<EmployeeRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Edit Roles Modal
  const [editingUser, setEditingUser] = useState<IamUser | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [changeReason, setChangeReason] = useState('');
  const [saving, setSaving] = useState(false);

  // Create User Modal
  const [createModal, setCreateModal] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [newPassword, setNewPassword] = useState('ChangeMe!2026');
  const [newEmployeeId, setNewEmployeeId] = useState('');
  const [newRoles, setNewRoles] = useState<string[]>(['employee']);

  async function loadData() {
    setLoading(true);
    try {
      const [uRes, empRes] = await Promise.all([
        listIamUsers(),
        listEmployees(),
      ]);
      setUsers(uRes);
      setEmployees(empRes.items);
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to load access control data' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
  }, []);

  function openEditModal(u: IamUser) {
    setEditingUser(u);
    setSelectedRoles(u.roleKeys && u.roleKeys.length > 0 ? [...u.roleKeys] : ['employee']);
    setChangeReason('');
  }

  async function handleSaveRoles() {
    if (!editingUser) return;
    if (selectedRoles.length === 0) {
      setMsg({ type: 'error', text: 'At least one role must be selected' });
      return;
    }
    setSaving(true);
    try {
      await updateIamUserRoles(editingUser.id, selectedRoles, changeReason);
      setMsg({ type: 'success', text: `Access roles updated successfully for ${editingUser.fullName}` });
      setEditingUser(null);
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to update roles' });
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleStatus(u: IamUser) {
    const nextStatus = u.status === 'active' ? 'suspended' : 'active';
    try {
      await updateIamUserStatus(u.id, nextStatus);
      setMsg({
        type: 'success',
        text: `User account for ${u.fullName} is now ${nextStatus}`,
      });
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to update user status' });
    }
  }

  async function handleCreateUser(e: React.FormEvent) {
    e.preventDefault();
    if (!newEmail || !newName) {
      setMsg({ type: 'error', text: 'Email and Full Name are required' });
      return;
    }
    setSaving(true);
    try {
      await createIamUser({
        email: newEmail,
        fullName: newName,
        password: newPassword,
        employeeId: newEmployeeId || null,
        roles: newRoles,
      });
      setMsg({ type: 'success', text: `User account created successfully for ${newName}` });
      setCreateModal(false);
      setNewEmail('');
      setNewName('');
      setNewEmployeeId('');
      setNewRoles(['employee']);
      await loadData();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to create user' });
    } finally {
      setSaving(false);
    }
  }

  const filteredUsers = users.filter((u) => {
    const matchSearch =
      u.fullName.toLowerCase().includes(search.toLowerCase()) ||
      u.email.toLowerCase().includes(search.toLowerCase()) ||
      (u.employee?.employeeCode && u.employee.employeeCode.toLowerCase().includes(search.toLowerCase()));

    const matchRole =
      roleFilter === 'all' || u.roleKeys.includes(roleFilter);

    return matchSearch && matchRole;
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Header Banner */}
      <div className="panel" style={{ padding: '24px 28px', background: 'linear-gradient(135deg, var(--bg-surface) 0%, rgba(79, 70, 229, 0.05) 100%)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: 'var(--brand-light)', color: 'var(--brand-primary)', display: 'grid', placeItems: 'center' }}>
                <Icon name="shieldCheck" size="md" />
              </div>
              <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Roles & Access Governance (IAM)</h1>
            </div>
            <p className="muted" style={{ margin: 0, fontSize: 13, maxWidth: 650 }}>
              Executive authority desk for <strong>CEO and Administrators</strong> to assign persona roles, configure security boundaries, link employee profiles, and grant internal system access.
            </p>
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button className="btn" onClick={() => setCreateModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="plus" size="sm" />
              <span>Create User Account</span>
            </button>
            <button className="btn-quiet" onClick={loadData} title="Refresh User List">
              <Icon name="refresh" size="sm" />
            </button>
          </div>
        </div>
      </div>

      {msg && (
        <div className={`banner banner-${msg.type}`} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{msg.text}</span>
          <button onClick={() => setMsg(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}>✕</button>
        </div>
      )}

      {/* KPI Overview */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
        <div className="panel" style={{ padding: '16px 20px' }}>
          <span className="eyebrow">Total Users</span>
          <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4 }}>{users.length}</div>
          <span className="muted" style={{ fontSize: 12 }}>Active login accounts</span>
        </div>

        <div className="panel" style={{ padding: '16px 20px' }}>
          <span className="eyebrow">Executive / Admins</span>
          <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4, color: '#3b82f6' }}>
            {users.filter((u) => u.roleKeys.some((r) => ['organization_owner', 'ceo_director', 'hr_admin'].includes(r))).length}
          </div>
          <span className="muted" style={{ fontSize: 12 }}>Owner, CEO & HR Admins</span>
        </div>

        <div className="panel" style={{ padding: '16px 20px' }}>
          <span className="eyebrow">Financial Controllers</span>
          <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4, color: '#10b981' }}>
            {users.filter((u) => u.roleKeys.some((r) => ['finance_manager', 'payroll_admin'].includes(r))).length}
          </div>
          <span className="muted" style={{ fontSize: 12 }}>Finance & Payroll Admins</span>
        </div>

        <div className="panel" style={{ padding: '16px 20px' }}>
          <span className="eyebrow">Staff & Managers</span>
          <div style={{ fontSize: 24, fontWeight: 700, marginTop: 4, color: '#ec4899' }}>
            {users.filter((u) => u.roleKeys.some((r) => ['manager', 'employee'].includes(r))).length}
          </div>
          <span className="muted" style={{ fontSize: 12 }}>Team leads & employees</span>
        </div>
      </div>

      {/* Available Persona Roles Reference Cards */}
      <div className="panel" style={{ padding: 20 }}>
        <span className="eyebrow">Persona Catalogue</span>
        <h3 style={{ margin: '2px 0 14px 0', fontSize: 15 }}>Assignable System Roles & Responsibility Scopes</h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
          {Object.entries(ROLE_INFO).map(([key, info]) => {
            const roleCount = users.filter((u) => u.roleKeys.includes(key)).length;
            return (
              <div key={key} style={{ padding: '14px 16px', background: 'var(--bg-field)', borderRadius: 10, border: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 24, height: 24, borderRadius: 6, background: `${info.color}20`, color: info.color, display: 'grid', placeItems: 'center' }}>
                      <Icon name={info.icon} size="xs" />
                    </div>
                    <strong style={{ fontSize: 13 }}>{info.label}</strong>
                  </div>
                  <span className="mono badge" style={{ fontSize: 11 }}>{roleCount} users</span>
                </div>
                <p className="muted" style={{ fontSize: 11, margin: 0, lineHeight: 1.4 }}>{info.desc}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* User Directory & Role Assignment Table */}
      <div className="panel" style={{ padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
          <div>
            <span className="eyebrow">User Directory</span>
            <h3 style={{ margin: '2px 0 0 0', fontSize: 16 }}>Internal Accounts & Assigned Access</h3>
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <input
              type="search"
              placeholder="Search by name, email or code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ minWidth: 260 }}
            />

            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
              <option value="all">All Persona Roles</option>
              <option value="organization_owner">Organisation Owner</option>
              <option value="ceo_director">CEO / Director</option>
              <option value="hr_admin">HR Admin</option>
              <option value="finance_manager">Finance Manager</option>
              <option value="payroll_admin">Payroll Admin</option>
              <option value="manager">Team Lead / Manager</option>
              <option value="employee">Employee (Self-Service)</option>
              <option value="auditor">Auditor</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="muted" style={{ textAlign: 'center', padding: 40 }}>Loading users and access privileges...</div>
        ) : filteredUsers.length === 0 ? (
          <div className="muted" style={{ textAlign: 'center', padding: 40 }}>No user accounts matched your search criteria.</div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>User & Contact</th>
                  <th>Linked Employee Profile</th>
                  <th>Assigned Persona Roles</th>
                  <th>Account Status</th>
                  <th>Last Active</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <div>
                        <strong>{u.fullName}</strong>
                        {u.id === session?.user?.id && (
                          <span className="badge" style={{ marginLeft: 6, fontSize: 10, background: 'var(--brand-light)', color: 'var(--brand-primary)' }}>You</span>
                        )}
                      </div>
                      <div className="muted mono" style={{ fontSize: 11 }}>{u.email}</div>
                    </td>

                    <td>
                      {u.employee ? (
                        <div>
                          <Link to={`/employees/${u.employeeId}`} style={{ fontWeight: 600, color: 'var(--brand-primary)' }}>
                            {u.employee.name}
                          </Link>
                          <div className="muted mono" style={{ fontSize: 11 }}>{u.employee.employeeCode} &bull; {u.employee.designation || 'Staff'}</div>
                        </div>
                      ) : (
                        <span className="muted" style={{ fontSize: 12 }}>Unlinked Account</span>
                      )}
                    </td>

                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {u.roles && u.roles.length > 0 ? (
                          u.roles.map((r) => {
                            const meta = ROLE_INFO[r.key] || { label: r.name, color: '#64748b' };
                            return (
                              <span
                                key={r.key}
                                className="badge"
                                style={{
                                  fontSize: 11,
                                  fontWeight: 600,
                                  background: `${meta.color}15`,
                                  color: meta.color,
                                  border: `1px solid ${meta.color}40`,
                                }}
                              >
                                {meta.label}
                              </span>
                            );
                          })
                        ) : (
                          <span className="muted" style={{ fontSize: 11 }}>No Roles</span>
                        )}
                      </div>
                    </td>

                    <td>
                      <StatusBadge status={u.status === 'active' ? 'verified' : 'draft'} label={u.status.toUpperCase()} />
                    </td>

                    <td className="muted mono" style={{ fontSize: 12 }}>
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : 'Never'}
                    </td>

                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
                        <button
                          className="btn-sm btn-primary"
                          onClick={() => openEditModal(u)}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        >
                          <Icon name="shieldCheck" size="xs" />
                          <span>Assign Roles</span>
                        </button>

                        <button
                          className="btn-sm btn-quiet"
                          onClick={() => handleToggleStatus(u)}
                          title={u.status === 'active' ? 'Suspend Login Access' : 'Activate Login Access'}
                        >
                          {u.status === 'active' ? 'Suspend' : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal: Edit Persona Roles */}
      {editingUser && (
        <div className="modal-backdrop" onClick={() => setEditingUser(null)}>
          <div className="modal" style={{ maxWidth: 580 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <span className="eyebrow">Security Governance</span>
                <h2 style={{ margin: '2px 0 0 0', fontSize: 18 }}>Assign Persona Roles & Access</h2>
              </div>
              <button onClick={() => setEditingUser(null)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer' }}>✕</button>
            </div>

            <div style={{ padding: '12px 16px', background: 'var(--bg-field)', borderRadius: 8, marginBottom: 18 }}>
              <div style={{ fontWeight: 600 }}>{editingUser.fullName}</div>
              <div className="muted mono" style={{ fontSize: 12 }}>{editingUser.email}</div>
              {editingUser.employee && (
                <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>
                  Linked Employee: {editingUser.employee.name} ({editingUser.employee.employeeCode})
                </div>
              )}
            </div>

            <div style={{ marginBottom: 18 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
                Select Internal Roles for this User:
              </label>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 260, overflowY: 'auto', paddingRight: 4 }}>
                {Object.entries(ROLE_INFO).map(([key, info]) => {
                  const isChecked = selectedRoles.includes(key);
                  return (
                    <label
                      key={key}
                      style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 10,
                        padding: '10px 12px',
                        borderRadius: 8,
                        border: isChecked ? `1px solid ${info.color}` : '1px solid var(--border-subtle)',
                        background: isChecked ? `${info.color}08` : 'var(--bg-surface)',
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedRoles([...selectedRoles, key]);
                          } else {
                            setSelectedRoles(selectedRoles.filter((r) => r !== key));
                          }
                        }}
                        style={{ marginTop: 3 }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <strong style={{ fontSize: 13, color: isChecked ? info.color : 'inherit' }}>{info.label}</strong>
                          <span className="mono" style={{ fontSize: 10, opacity: 0.7 }}>({key})</span>
                        </div>
                        <div className="muted" style={{ fontSize: 11, marginTop: 2, lineHeight: 1.3 }}>{info.desc}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontWeight: 600, fontSize: 13, marginBottom: 4 }}>
                Audit Reason (Optional):
              </label>
              <input
                type="text"
                placeholder="e.g. Promoted to Team Lead / Transferred to Finance"
                value={changeReason}
                onChange={(e) => setChangeReason(e.target.value)}
                style={{ width: '100%' }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button className="btn-quiet" onClick={() => setEditingUser(null)}>Cancel</button>
              <button className="btn" onClick={handleSaveRoles} disabled={saving}>
                {saving ? 'Saving Roles...' : 'Confirm & Apply Access'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Create User Account */}
      {createModal && (
        <div className="modal-backdrop" onClick={() => setCreateModal(false)}>
          <div className="modal" style={{ maxWidth: 540 }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <div>
                <span className="eyebrow">User Management</span>
                <h2 style={{ margin: '2px 0 0 0', fontSize: 18 }}>Create Internal Login Account</h2>
              </div>
              <button onClick={() => setCreateModal(false)} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer' }}>✕</button>
            </div>

            <form onSubmit={handleCreateUser} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 12, marginBottom: 4 }}>Work Email *</label>
                <input
                  type="email"
                  required
                  placeholder="name@abny.cloud"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 12, marginBottom: 4 }}>Full Name *</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Rahul Sharma"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  style={{ width: '100%' }}
                />
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 12, marginBottom: 4 }}>Initial Password</label>
                <input
                  type="text"
                  placeholder="ChangeMe!2026"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={{ width: '100%' }}
                />
                <span className="muted" style={{ fontSize: 11 }}>User will be prompted to change upon first login</span>
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 12, marginBottom: 4 }}>Link to Employee Profile (Optional)</label>
                <select
                  value={newEmployeeId}
                  onChange={(e) => {
                    setNewEmployeeId(e.target.value);
                    const found = employees.find((emp) => emp.id === e.target.value);
                    if (found) {
                      if (!newName) setNewName(`${found.first_name} ${found.last_name}`);
                      if (!newEmail && (found.work_email || found.personal_email)) {
                        setNewEmail((found.work_email || found.personal_email)!);
                      }
                    }
                  }}
                  style={{ width: '100%' }}
                >
                  <option value="">-- Standalone User (No Employee Link) --</option>
                  {employees.map((emp) => (
                    <option key={emp.id} value={emp.id}>
                      {emp.first_name} {emp.last_name} ({emp.employee_code}) - {emp.designation || 'Staff'}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={{ display: 'block', fontWeight: 600, fontSize: 12, marginBottom: 6 }}>Assign Initial Roles:</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                  {Object.entries(ROLE_INFO).map(([key, info]) => {
                    const isChecked = newRoles.includes(key);
                    return (
                      <label
                        key={key}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          fontSize: 12,
                          padding: '6px 8px',
                          borderRadius: 6,
                          background: isChecked ? `${info.color}15` : 'var(--bg-field)',
                          border: isChecked ? `1px solid ${info.color}` : '1px solid var(--border-subtle)',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setNewRoles([...newRoles, key]);
                            } else {
                              setNewRoles(newRoles.filter((r) => r !== key));
                            }
                          }}
                        />
                        <span>{info.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 10 }}>
                <button type="button" className="btn-quiet" onClick={() => setCreateModal(false)}>Cancel</button>
                <button type="submit" className="btn" disabled={saving}>
                  {saving ? 'Creating...' : 'Create Account & Grant Access'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
